import { useCallback, useEffect, useState } from 'react';
import { api, ApiRequestError } from '../lib/api';
import { Button, Card, Empty, ErrorNote, Label, Loading, Screen } from '../components/ui';
import type { ReviewResponse } from '../../shared/types';

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <span className="text-xl font-semibold text-mist-200">{value}</span>
    </div>
  );
}

export function Review() {
  const [data, setData] = useState<ReviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.review());
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load the review.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function generate(force: boolean) {
    setBusy(true);
    try {
      setData(await api.generateReview({ force }));
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not write the review.');
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return (
      <Screen title="Review">
        {error ? <ErrorNote>{error}</ErrorNote> : <Loading label="Loading the review" />}
      </Screen>
    );
  }

  const { metrics } = data;

  return (
    <Screen title={`Review · ${data.date}`}>
      <Card className="grid grid-cols-2 gap-5">
        <Stat label="Reps" value={`${metrics.completedToday}/${metrics.plannedToday}`} />
        <Stat label="Streak" value={`${metrics.currentStreak} d`} />
        <Stat label="Anxiety before" value={metrics.meanAnxietyBefore?.toString() ?? '—'} />
        <Stat label="Anxiety after" value={metrics.meanAnxietyAfter?.toString() ?? '—'} />
        <Stat label="Median hesitation" value={metrics.medianHesitation ?? '—'} />
        <Stat label="All time" value={`${metrics.completedAllTime}`} />
      </Card>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      {data.review ? (
        <>
          <Card className="animate-rep flex flex-col gap-3">
            <Label>Today&rsquo;s observation</Label>
            <p className="text-base leading-relaxed text-mist-200">{data.review.observation}</p>
          </Card>
          <Card className="flex flex-col gap-3">
            <Label>Tomorrow</Label>
            <p className="text-base leading-relaxed text-mist-200">
              {data.review.tomorrowStrategy}
            </p>
            {data.tomorrowPlan ? (
              <p className="text-xs text-mist-400">
                Stage {data.tomorrowPlan.stage} · focus {data.tomorrowPlan.focus ?? 'volume'}
              </p>
            ) : null}
          </Card>
          <Button variant="ghost" onClick={() => void generate(true)} disabled={busy}>
            {busy ? 'Rewriting…' : 'Write it again'}
          </Button>
        </>
      ) : (
        <Empty title="No review for today yet.">
          <Button variant="primary" full onClick={() => void generate(false)} disabled={busy}>
            {busy ? 'Thinking…' : 'End the day'}
          </Button>
        </Empty>
      )}
    </Screen>
  );
}
